import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE } from '../config';
import { useTranslation } from '../translations';
import {
  PanelContainer, Title, PageHeader, HeaderActions, TitleRow,
  PrimaryButton, SecondaryButton, IconButton, Badge,
  FieldLabel, FieldHint, RequiredMark, TextInput, TextArea,
} from '../ui/common';
import { DownloadIcon, AlertIcon, TrashIcon, MailIcon, EditIcon, ArrowLeftIcon, CheckIcon, LockIcon } from '../ui/icons';
import { Modal, useConfirm } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import { Banner } from '../components/ui/Banner';
import { docTypeInfo, paymentStatusInfo, isOverdue, paymentProgress } from '../utils/invoiceStatus';
import { formatMoney, formatDate } from '../utils/format';
import { pozycjaKorekty, kluczStawki, KOD_ZWOLNIENIA } from '../utils/invoiceItems';

// ── Style ──────────────────────────────────────────────────────────────────
// Do tej pory ta strona miala WLASNY kontener (inny padding, cien --shadow-lg, brak wlosa na
// gornej krawedzi), wlasny tytul 1.6rem i wlasny przycisk. Teraz stoi na tych samych prymitywach
// co reszta panelu. Uklad: dokument po lewej, szyna „Rozliczenie" po prawej — ta sama, co
// podsumowanie w edytorze faktury, wiec obie strony dokumentu maja jeden szkielet.

// PanelContainer przycina zawartosc (overflow: hidden), a przyciety przodek wylacza
// position: sticky u potomkow. Szyna po prawej opiera sie na sticky (jak w
// components/document/builderChrome.js), a nic tu nie wystaje poza kontener.
const Page = styled(PanelContainer)`
  overflow: visible;
`;
const HeaderMeta = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--sp-2);
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;
const LoadingText = styled.p`
  margin: 0;
  padding: var(--sp-8) 0;
  text-align: center;
  color: var(--text-muted);
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: var(--sp-6);
  align-items: start;

  /* Na wezszym ekranie rozliczenie idzie PRZED dokumentem: na telefonie ramka PDF zajmuje caly
     ekran, a stan platnosci i „Zarejestruj platnosc" sa tym, po co sie tu zwykle wchodzi. */
  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
    & > aside { order: -1; }
  }
`;
const DocumentFrame = styled.div`
  border: 1px solid var(--input-border);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-sm);
  background: var(--card-bg);
  overflow: hidden;
`;
const PdfFrame = styled.iframe`
  display: block;
  width: 100%;
  height: calc(100vh - 220px);
  min-height: 640px;
  border: 0;
  /* Swiadomy wyjatek od tokenow: to tlo POD strona PDF, ktora sama jest biala jak papier
     niezaleznie od motywu panelu — token --card-bg dawalby granatowy pasek widoczny w
     szparach podczas ladowania w trybie ciemnym. */
  background: #fff;

  @media (max-width: 768px) {
    height: 75vh;
    min-height: 480px;
  }
`;
const FramePlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 480px;
  padding: var(--sp-6);
  text-align: center;
  color: var(--text-muted);
  font-size: var(--fs-sm);
`;
const PdfError = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-3);
`;

const Rail = styled.aside`
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-sm);

  @media (min-width: 1101px) {
    position: sticky;
    top: var(--sp-6);
  }
`;
const RailSection = styled.section`
  padding: var(--sp-5);
  & + & { border-top: 1px solid var(--border); }
`;
const Eyebrow = styled.h2`
  margin: 0 0 var(--sp-3);
  font-size: var(--fs-xs);
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--text-muted);
`;

// Rozliczenie: kwota dokumentu, pasek wplat i jedna akcja. Pasek ma sens tylko dla dodatniej
// kwoty (korekta bywa ujemna), wiec przy korekcie go nie ma.
const Amount = styled.div`
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: var(--text);
`;
const AmountCaption = styled.div`
  margin-top: 2px;
  font-size: var(--fs-sm);
  color: var(--text-muted);
`;
const Meter = styled.div`
  height: 8px;
  margin: var(--sp-4) 0 var(--sp-2);
  border-radius: var(--r-pill);
  background: var(--border);
  overflow: hidden;
`;
const MeterFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: var(--success);
  transition: width var(--dur-slow) var(--ease);
  @media (prefers-reduced-motion: reduce) { transition: none; }
`;
const MeterLegend = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--sp-1) var(--sp-3);
  font-size: var(--fs-sm);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
`;
const StateLine = styled.p`
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin: var(--sp-3) 0 0;
  font-size: var(--fs-sm);
  font-weight: 600;
  color: var(--${(p) => p.$tone || 'text-muted'});
`;
const RailPrimary = styled(PrimaryButton)`
  width: 100%;
  margin-top: var(--sp-4);
`;
const RailSecondary = styled(SecondaryButton)`
  width: 100%;
  margin-top: var(--sp-3);
`;

const DetailList = styled.dl`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: var(--sp-2) var(--sp-4);
  margin: 0;
  font-size: var(--fs-sm);

  dt { color: var(--text-muted); }
  dd {
    margin: 0;
    text-align: right;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }
  dd.overdue { color: var(--danger); }
`;
const PartyText = styled.div`
  font-size: var(--fs-sm);
  line-height: 1.6;
  color: var(--text-muted);
  overflow-wrap: anywhere;

  strong { display: block; font-size: var(--fs-base); color: var(--text); }
`;
const LockNote = styled.p`
  display: flex;
  align-items: flex-start;
  gap: var(--sp-2);
  margin: 0;
  font-size: var(--fs-sm);
  line-height: 1.5;
  color: var(--text-muted);

  svg { margin-top: 2px; }
`;

// Usuniecie proformy: odmiana SecondaryButton w kolorze zagrozenia (obrys, nie pelne tlo),
// bo stoi w rzedzie spokojnych akcji i nie ma krzyczec glosniej niz one.
const DeleteButton = styled(SecondaryButton)`
  color: var(--danger);
  border-color: var(--danger-border);
  &:hover:not(:disabled) { background: var(--danger-bg); }
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
`;
const Hint = styled.p`
  margin: 0 0 var(--sp-4);
  font-size: var(--fs-sm);
  line-height: 1.5;
  color: var(--text-muted);
`;
const ModalFooterRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
`;

export default function InvoicePreview() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, currentLanguage } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState(null);
  // null albo 'busy' (kolejka PDF pełna, 503 z serwera) albo 'error'.
  const [pdfFailed, setPdfFailed] = useState(null);
  const [pdfAttempt, setPdfAttempt] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [showCorrectModal, setShowCorrectModal] = useState(false);
  const [correctReason, setCorrectReason] = useState('');
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const money = (value) => formatMoney(value, { locale: currentLanguage });
  const date = (value) => formatDate(value, { locale: currentLanguage });

  // `silent`: odswiezenie po akcji (platnosc, e-mail) bez zamiany calej strony na „Ladowanie",
  // ktore odmontowywalo ramke PDF i kazalo generowac podglad od nowa.
  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/invoices/${id}`, { credentials: 'include' });
      if (!res.ok) throw new Error(t('invoices.preview.notFound'));
      const data = await res.json();
      setInvoice(data);
      setError(null);
      // Pole to kwota TEJ wpłaty (dopisywana do dotychczasowych), więc podpowiedzią jest
      // pozostała do zapłaty, a nie cała kwota faktury.
      const remaining = Math.round((Number(data.invoice.total_gross || 0) - Number(data.invoice.amount_paid || 0)) * 100) / 100;
      setPaymentAmount(remaining > 0 ? String(remaining) : '');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    /* eslint-disable-next-line */
  }, [id]);

  // Podglad PDF. Wyjscie ze strony albo przejscie do innej faktury PRZERYWA zadanie (12.10.2026):
  // render czekal dalej w kolejce PDF i zajmowal jedno z szesciu miejsc firmy, wiec szybkie
  // przegladanie faktur przez kilka osob konczylo sie bledem podgladu. Pelna kolejka (503) daje
  // osobny komunikat z przyciskiem „Sprobuj ponownie”, a nie ogolny blad pobierania.
  useEffect(() => {
    let objectUrl = null;
    const przerwij = typeof AbortController === 'function' ? new AbortController() : null;
    setPdfUrl(null);
    setPdfFailed(null);
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/invoices/${id}/pdf`, {
          credentials: 'include',
          ...(przerwij ? { signal: przerwij.signal } : {}),
        });
        if (!r.ok) {
          if (!(przerwij && przerwij.signal.aborted)) setPdfFailed(r.status === 503 ? 'busy' : 'error');
          return;
        }
        const blob = await r.blob();
        if (przerwij && przerwij.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      } catch (_) {
        if (!(przerwij && przerwij.signal.aborted)) setPdfFailed('error');
      }
    })();
    // Zwolnienie bloba przy zmianie faktury (np. przejscie do wystawionej korekty) i wyjsciu.
    // Wczesniej cleanup czytal `pdfUrl` z domkniecia pierwszego renderu, czyli zawsze null.
    return () => {
      if (przerwij) przerwij.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, pdfAttempt]);

  const downloadPdf = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/invoices/${id}/pdf`, { credentials: 'include' });
      if (res.status === 503) { toast.error(t('invoices.pdf.busy')); return; }
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice.invoice_number.replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error(t('invoices.pdf.downloadError'));
    }
  };

  const sendEmail = async () => {
    if (!invoice.invoice.buyer_email) {
      toast.error(t('invoices.preview.noBuyerEmail'));
      return;
    }
    const ok = await confirm({
      title: t('invoices.preview.sendEmailConfirmTitle'),
      message: t('invoices.preview.sendEmailConfirmMessage', { email: invoice.invoice.buyer_email }),
      tone: 'primary',
      confirmLabel: t('invoices.preview.sendEmail'),
    });
    if (!ok) return;
    setActionLoading(true);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/invoices/${id}/send-email`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.error'));
      toast.success(data.message);
      load({ silent: true });
    } catch (e) {
      toast.error(t('invoices.preview.actionError', { message: e.message }));
    } finally {
      setActionLoading(false);
    }
  };

  const savePayment = async () => {
    const amount = Number(paymentAmount);
    if (!(amount > 0)) { toast.error(t('invoices.preview.paymentAmountInvalid')); return; }
    setActionLoading(true);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      // `add_amount`: serwer DOPISUJE wpłatę do dotychczasowych. Wcześniej wysyłaliśmy wpisaną
      // kwotę jako łączną, więc druga rata nadpisywała pierwszą.
      const res = await fetch(`${API_BASE}/api/invoices/${id}/payment`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ add_amount: amount })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.error'));
      setShowPaymentModal(false);
      toast.success(t('common.savedSuccess'));
      load({ silent: true });
      // PDF zależy od stanu zapłaty (blok „Zapłacono / Pozostało”, dane do przelewu i termin tylko
      // przy należności), więc podgląd pobieramy od nowa. Bez tego ramka zostawała z dokumentem
      // sprzed wpłaty, choć szyna obok mówiła „Opłacona w całości” (przegląd PR #212).
      setPdfAttempt((n) => n + 1);
    } catch (e) {
      toast.error(t('invoices.preview.actionError', { message: e.message }));
    } finally {
      setActionLoading(false);
    }
  };

  const submitCorrection = async () => {
    if (!correctReason.trim()) { toast.error(t('invoices.preview.correctReasonRequired')); return; }
    setActionLoading(true);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      // Korekta to pełne storno: zeruje wszystkie pozycje i jest wystawiana od razu (jak mówi
      // okno). Korekty nie da się potem edytować — PUT przyjmuje wyłącznie proformy.
      // Brutto oryginału z minusem (utils/invoiceItems.js): storno zeruje każdą pozycję co do
      // grosza, także pozycję z rezerwacji policzoną od brutto (12.10.2026).
      const items = invoice.items.map(pozycjaKorekty);
      const res = await fetch(`${API_BASE}/api/invoices/${id}/correct`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-csrf-token': csrf },
        body: JSON.stringify({ items, reason: correctReason })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('common.error'));
      setShowCorrectModal(false);
      navigate(`/invoices/${data.invoice.id}`);
    } catch (e) {
      toast.error(t('invoices.preview.actionError', { message: e.message }));
    } finally {
      setActionLoading(false);
    }
  };

  const deleteInvoice = async () => {
    const ok = await confirm({
      title: t('invoices.preview.deleteConfirmTitle'),
      message: t('invoices.preview.deleteConfirmMessage'),
      tone: 'danger',
      confirmLabel: t('common.delete'),
    });
    if (!ok) return;
    setActionLoading(true);
    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const res = await fetch(`${API_BASE}/api/invoices/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'x-csrf-token': csrf }
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || t('common.error'));
      }
      navigate('/invoices');
    } catch (e) {
      toast.error(t('invoices.preview.actionError', { message: e.message }));
      setActionLoading(false);
    }
  };

  const backButton = (
    <IconButton onClick={() => navigate('/invoices')} aria-label={t('invoices.preview.backToList')}>
      <ArrowLeftIcon />
    </IconButton>
  );

  if (loading) {
    return <PanelContainer><LoadingText>{t('common.loading')}</LoadingText></PanelContainer>;
  }
  if (error) {
    return (
      <PanelContainer>
        <PageHeader>
          <TitleRow>
            {backButton}
            <Title>{t('invoices.title')}</Title>
          </TitleRow>
        </PageHeader>
        <Banner tone="danger" action={{ label: t('invoices.preview.backToList'), onClick: () => navigate('/invoices') }}>
          {error}
        </Banner>
      </PanelContainer>
    );
  }
  if (!invoice) return null;

  const inv = invoice.invoice;
  const isProforma = inv.doc_type === 'PROFORMA';
  // Korekta nie jest należnością — ma ujemną kwotę i zmienia fakturę, którą koryguje. Odznaka
  // „Nieopłacona" i przycisk wpłaty sugerowały, że klient ma ją jeszcze zapłacić.
  const isCorrection = inv.doc_type === 'CORRECTION';
  const isVatIssued = inv.doc_type === 'VAT' && inv.status === 'issued';
  const isPaid = inv.payment_status === 'paid';
  const docType = docTypeInfo(inv.doc_type);
  // Faktura firmy zwolnionej z VAT (same pozycje „zw”) nazywa się „Faktura”, jak na PDF (F.15).
  const pozycje = invoice.items || [];
  const bezVat = inv.doc_type === 'VAT' && pozycje.length > 0 && pozycje.every((it) => kluczStawki(it) === KOD_ZWOLNIENIA);
  const tytulKey = bezVat ? 'invoices.docType.vatExempt' : docType.labelKey;
  const paymentStatus = paymentStatusInfo(inv.payment_status);
  const progress = paymentProgress(inv);
  const overdue = isOverdue(inv);

  return (
    <Page>
      <PageHeader>
        <TitleRow>
          {backButton}
          <div>
            <Title>{tytulKey ? t(tytulKey) : inv.doc_type} {inv.invoice_number}</Title>
            <HeaderMeta>
              {!isCorrection && (
                <Badge data-cy="invoice-preview-payment-status" $tone={paymentStatus.tone}>
                  {paymentStatus.labelKey ? t(paymentStatus.labelKey) : inv.payment_status}
                </Badge>
              )}
              {inv.status === 'corrected' && <Badge $tone="danger">{t('invoices.preview.correctedBadge')}</Badge>}
              {inv.status === 'draft' && <Badge $tone="info">{t('invoices.preview.draftBadge')}</Badge>}
              <span>{inv.buyer_name}</span>
            </HeaderMeta>
          </div>
        </TitleRow>
        <HeaderActions>
          <SecondaryButton onClick={downloadPdf}><DownloadIcon size={16} /> {t('invoices.preview.downloadPdf')}</SecondaryButton>
          {inv.buyer_email && !isProforma && (
            <SecondaryButton onClick={sendEmail} disabled={actionLoading}><MailIcon size={16} /> {t('invoices.preview.sendEmail')}</SecondaryButton>
          )}
          {isProforma && inv.status === 'draft' && (
            <SecondaryButton onClick={() => navigate(`/invoices/${id}/edit`)}><EditIcon size={16} /> {t('common.edit')}</SecondaryButton>
          )}
          {isProforma && (
            <DeleteButton onClick={deleteInvoice} disabled={actionLoading}><TrashIcon size={16} /> {t('common.delete')}</DeleteButton>
          )}
        </HeaderActions>
      </PageHeader>

      <Layout>
        <DocumentFrame>
          {pdfUrl ? (
            /* navpanes=0: bez paska miniatur, ktory przy jednostronicowej fakturze zabieral
               jedna czwarta ramki; view=FitH: strona na szerokosc ramki. */
            <PdfFrame src={`${pdfUrl}#navpanes=0&view=FitH`} title={t('invoices.preview.pdfPreviewTitle')} />
          ) : (
            <FramePlaceholder data-cy="invoice-pdf-placeholder">
              {pdfFailed ? (
                <PdfError>
                  <span>{pdfFailed === 'busy' ? t('invoices.pdf.busy') : t('invoices.pdf.downloadError')}</span>
                  <SecondaryButton type="button" onClick={() => setPdfAttempt((n) => n + 1)} data-cy="invoice-pdf-retry">
                    {t('invoices.pdf.retry')}
                  </SecondaryButton>
                </PdfError>
              ) : t('invoices.preview.generatingPdf')}
            </FramePlaceholder>
          )}
        </DocumentFrame>

        <Rail>
          <RailSection>
            <Eyebrow>{t('invoices.preview.settlement')}</Eyebrow>
            <Amount>{money(inv.total_gross)}</Amount>
            <AmountCaption>{t('invoices.preview.invoiceTotal')}</AmountCaption>

            {progress.total > 0 && (
              <>
                <Meter
                  role="progressbar"
                  aria-label={t('invoices.preview.paymentProgress')}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(progress.ratio * 100)}
                >
                  <MeterFill style={{ width: `${progress.ratio * 100}%` }} />
                </Meter>
                <MeterLegend>
                  <span>{t('invoices.preview.paidAmount', { amount: money(progress.paid) })}</span>
                  {!isPaid && <span>{t('invoices.preview.remainingAmount', { amount: money(progress.remaining) })}</span>}
                </MeterLegend>
              </>
            )}

            {overdue && (
              <StateLine $tone="danger" data-cy="invoice-preview-overdue">
                <AlertIcon size={16} /> {t('invoices.preview.overdueSince', { date: date(inv.payment_due_date) })}
              </StateLine>
            )}

            {isCorrection ? (
              <StateLine data-cy="invoice-correction-no-payment">{t('invoices.preview.correctionNoPayment')}</StateLine>
            ) : isPaid ? (
              <StateLine $tone="success"><CheckIcon size={16} /> {t('invoices.preview.paidInFull')}</StateLine>
            ) : (
              <RailPrimary data-cy="invoice-register-payment" onClick={() => setShowPaymentModal(true)}>
                {t('invoices.preview.registerPayment')}
              </RailPrimary>
            )}
          </RailSection>

          <RailSection>
            <Eyebrow>{t('invoices.preview.details')}</Eyebrow>
            <DetailList>
              <dt>{t('invoices.fields.issueDate')}</dt>
              <dd>{date(inv.issue_date)}</dd>
              <dt>{t('invoices.fields.saleDate')}</dt>
              <dd>{date(inv.sale_date)}</dd>
              {inv.payment_due_date && (
                <>
                  <dt>{t('invoices.fields.dueDate')}</dt>
                  <dd className={overdue ? 'overdue' : undefined}>{date(inv.payment_due_date)}</dd>
                </>
              )}
              {inv.payment_method && (
                <>
                  <dt>{t('invoices.fields.paymentMethod')}</dt>
                  <dd>{t(`invoices.paymentMethod.${inv.payment_method}`)}</dd>
                </>
              )}
            </DetailList>
          </RailSection>

          <RailSection>
            <Eyebrow>{t('invoices.fields.buyer')}</Eyebrow>
            <PartyText>
              <strong>{inv.buyer_name}</strong>
              {inv.buyer_nip && <div>{t('invoices.nipLabel', { nip: inv.buyer_nip })}</div>}
              {inv.buyer_address && <div>{inv.buyer_address}</div>}
              {inv.buyer_email && <div>{inv.buyer_email}</div>}
              {inv.buyer_phone && <div>{inv.buyer_phone}</div>}
            </PartyText>
          </RailSection>

          {inv.correction_reason && (
            <RailSection>
              <Eyebrow>{t('invoices.preview.correctReasonLabel')}</Eyebrow>
              <PartyText>{inv.correction_reason}</PartyText>
            </RailSection>
          )}

          {/* U.13 (FAK-07): wystawionej faktury VAT nie da się edytować ani usunąć — mówimy to
              wprost, stałym tekstem obok jedynej drogi zmiany (korekty). Wcześniej były tu dwa
              wyszarzone przyciski z objaśnieniem w dymku `title`, którego nie widać dotykiem. */}
          {isVatIssued && (
            <RailSection>
              <Eyebrow>{t('invoices.preview.correctionSection')}</Eyebrow>
              <LockNote data-cy="invoice-vat-locked-actions">
                <LockIcon size={16} />
                <span>{t('invoices.preview.vatLockedBanner')}</span>
              </LockNote>
              <RailSecondary onClick={() => setShowCorrectModal(true)}>
                <EditIcon size={16} /> {t('invoices.preview.issueCorrection')}
              </RailSecondary>
            </RailSection>
          )}
        </Rail>
      </Layout>

      <Modal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title={t('invoices.preview.paymentModalTitle')}
        size="sm"
        footer={
          <ModalFooterRow>
            <SecondaryButton onClick={() => setShowPaymentModal(false)} disabled={actionLoading}>{t('common.cancel')}</SecondaryButton>
            <PrimaryButton data-cy="invoice-save-payment" onClick={savePayment} disabled={actionLoading}>
              {actionLoading ? t('common.saving') : t('invoices.preview.savePayment')}
            </PrimaryButton>
          </ModalFooterRow>
        }
      >
        <ModalBody>
          <FieldLabel htmlFor="invoice-payment-amount">{t('invoices.preview.paymentModalAmountLabel')}</FieldLabel>
          <TextInput
            id="invoice-payment-amount"
            data-cy="invoice-payment-amount"
            type="number"
            step="0.01"
            min="0"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
          />
          <FieldHint>
            {t('invoices.preview.paymentModalInvoiceAmount', {
              amount: money(inv.total_gross),
              paid: money(progress.paid),
              remaining: money(progress.remaining),
            })}
          </FieldHint>
        </ModalBody>
      </Modal>

      <Modal
        open={showCorrectModal}
        onClose={() => setShowCorrectModal(false)}
        title={t('invoices.preview.correctModalTitle')}
        size="sm"
        footer={
          <ModalFooterRow>
            <SecondaryButton onClick={() => setShowCorrectModal(false)} disabled={actionLoading}>{t('common.cancel')}</SecondaryButton>
            <PrimaryButton onClick={submitCorrection} disabled={actionLoading || !correctReason.trim()}>
              {actionLoading ? t('invoices.preview.creating') : t('invoices.preview.issueCorrectionSubmit')}
            </PrimaryButton>
          </ModalFooterRow>
        }
      >
        <ModalBody>
          <Hint>{t('invoices.preview.correctModalDescription')}</Hint>
          <FieldLabel htmlFor="invoice-correct-reason">
            {t('invoices.preview.correctReasonLabel')}<RequiredMark>*</RequiredMark>
          </FieldLabel>
          <TextArea
            id="invoice-correct-reason"
            value={correctReason}
            onChange={(e) => setCorrectReason(e.target.value)}
            placeholder={t('invoices.preview.correctReasonPlaceholder')}
          />
        </ModalBody>
      </Modal>
    </Page>
  );
}
